import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useTranslation } from '../../locales/useTranslation'
import Button from '../shared/Button'
import Input from '../shared/Input'

const DAYS = [
  { id: 0, short: 'Ma', full: 'Maandag' },
  { id: 1, short: 'Di', full: 'Dinsdag' },
  { id: 2, short: 'Wo', full: 'Woensdag' },
  { id: 3, short: 'Do', full: 'Donderdag' },
  { id: 4, short: 'Vr', full: 'Vrijdag' },
  { id: 5, short: 'Za', full: 'Zaterdag' },
  { id: 6, short: 'Zo', full: 'Zondag' },
]

const CONNECTOR_TYPES = ['Type 2', 'CCS', 'CHAdeMO', 'Type 1']
const SPEEDS = ['3.7', '7.4', '11', '22', '50', '100']

const PRESETS = [
  { label: 'Werkdagen', days: [0, 1, 2, 3, 4], start: '08:00', end: '22:00' },
  { label: 'Weekend', days: [5, 6], start: '00:00', end: '23:59' },
  { label: 'Altijd', days: [0, 1, 2, 3, 4, 5, 6], start: '00:00', end: '23:59' },
]

const geocodeAddress = async (address, city, postalCode) => {
  const query = `${address}, ${postalCode} ${city}, Netherlands`
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&countrycodes=nl`
  const response = await fetch(url, {
    headers: { 'Accept-Language': 'nl', 'User-Agent': 'Buurtlader/1.0' }
  })
  const data = await response.json()
  if (data && data.length > 0) {
    return {
      latitude: parseFloat(data[0].lat),
      longitude: parseFloat(data[0].lon)
    }
  }
  throw new Error('Adres niet gevonden. Controleer het adres en probeer opnieuw.')
}

const ChargerForm = ({ session, onSuccess, onCancel, showToast }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [geocoding, setGeocoding] = useState(false)
  const [step, setStep] = useState(1)

  const [form, setForm] = useState({
    name: '',
    address: '',
    city: '',
    postal_code: '',
    connector_type: 'Type 2',
    speed_kw: '11',
    price_per_kwh: '',
  })

  // Availability: map of day_id -> array of {start, end}
  const [availability, setAvailability] = useState({})

  const updateForm = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const toggleDay = (dayId) => {
    setAvailability(prev => {
      if (prev[dayId]) {
        const next = { ...prev }
        delete next[dayId]
        return next
      } else {
        return { ...prev, [dayId]: [{ start: '08:00', end: '22:00' }] }
      }
    })
  }

  const applyPreset = (preset) => {
    const next = {}
    preset.days.forEach(d => {
      next[d] = [{ start: preset.start, end: preset.end }]
    })
    setAvailability(next)
  }

  const updateSlot = (dayId, slotIndex, key, value) => {
    setAvailability(prev => ({
      ...prev,
      [dayId]: prev[dayId].map((slot, i) =>
        i === slotIndex ? { ...slot, [key]: value } : slot
      )
    }))
  }

  const addSlot = (dayId) => {
    setAvailability(prev => ({
      ...prev,
      [dayId]: [...(prev[dayId] || []), { start: '08:00', end: '22:00' }]
    }))
  }

  const removeSlot = (dayId, slotIndex) => {
    setAvailability(prev => {
      const slots = prev[dayId].filter((_, i) => i !== slotIndex)
      if (slots.length === 0) {
        const next = { ...prev }
        delete next[dayId]
        return next
      }
      return { ...prev, [dayId]: slots }
    })
  }

  const handleStep1 = () => {
    if (!form.name || !form.address || !form.city || !form.postal_code) {
      return showToast('Vul alle verplichte velden in', true)
    }
    if (!form.price_per_kwh || isNaN(form.price_per_kwh) || parseFloat(form.price_per_kwh) <= 0) {
      return showToast('Vul een geldige prijs in', true)
    }
    const postalRegex = /^\d{4}[A-Z]{2}$/
    if (!postalRegex.test(form.postal_code.replace(' ', '').toUpperCase())) {
      return showToast('Vul een geldige postcode in (bijv. 1234AB)', true)
    }
    setStep(2)
  }

  const handleSubmit = async () => {
    if (Object.keys(availability).length === 0) {
      return showToast('Selecteer minstens één beschikbare dag', true)
    }

    setLoading(true)
    try {
      setGeocoding(true)
      const coords = await geocodeAddress(
        form.address,
        form.city,
        form.postal_code.replace(' ', '').toUpperCase()
      )
      setGeocoding(false)

      const { data: charger, error: chargerError } = await supabase
        .from('chargers')
        .insert({
          owner_id: session.user.id,
          name: form.name,
          address: form.address,
          city: form.city,
          postal_code: form.postal_code.replace(' ', '').toUpperCase(),
          latitude: coords.latitude,
          longitude: coords.longitude,
          connector_type: form.connector_type,
          speed_kw: parseFloat(form.speed_kw),
          price_per_kwh: parseFloat(form.price_per_kwh),
          is_available: true,
          status: 'actief'
        })
        .select()
        .single()

      if (chargerError) throw chargerError

      // Build availability slots from the map
      const slots = []
      Object.entries(availability).forEach(([dayId, daySlots]) => {
        daySlots.forEach(slot => {
          slots.push({
            charger_id: charger.id,
            day_of_week: parseInt(dayId),
            start_time: slot.start,
            end_time: slot.end
          })
        })
      })

      const { error: availError } = await supabase
        .from('charger_availability')
        .insert(slots)

      if (availError) throw availError

      await supabase.from('events').insert({
        user_id: session.user.id,
        event_type: 'charger_registered',
        entity_type: 'charger',
        entity_id: charger.id,
        metadata: {
          city: form.city,
          postal_code: form.postal_code,
          connector_type: form.connector_type,
          speed_kw: form.speed_kw
        }
      })

      onSuccess()

    } catch (error) {
      setGeocoding(false)
      showToast(error.message || t('fout'), true)
    } finally {
      setLoading(false)
    }
  }

  const selectedDayCount = Object.keys(availability).length

  return (
    <div style={{
      height: '100%',
      overflowY: 'auto',
      padding: '52px 18px 30px'
    }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          onClick={step === 1 ? onCancel : () => setStep(1)}
          style={{
            background: 'var(--cream-dark)',
            border: 'none',
            borderRadius: 10,
            padding: '8px 14px',
            cursor: 'pointer',
            fontSize: 16,
            color: 'var(--ink)',
            fontFamily: 'DM Sans, sans-serif'
          }}>
          ←
        </button>
        <div>
          <div style={{
            fontFamily: 'Fraunces, serif',
            fontSize: 22,
            fontWeight: 900,
            color: 'var(--ink)'
          }}>
            {t('laadpaal_registreren')}
          </div>
          <div style={{ color: 'var(--ink-muted)', fontSize: 12 }}>
            Stap {step} van 2 — {step === 1 ? 'Laadpaal details' : 'Beschikbaarheid'}
          </div>
        </div>
      </div>

      {/* Step indicators */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
        {[1, 2].map(s => (
          <div key={s} style={{
            height: 4,
            flex: 1,
            borderRadius: 2,
            background: s <= step ? 'var(--green)' : 'var(--cream-dark)',
            transition: 'background 0.3s'
          }} />
        ))}
      </div>

      {step === 1 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          <Input
            label={t('laadpaal_naam')}
            value={form.name}
            onChange={v => updateForm('name', v)}
            placeholder={t('laadpaal_naam_placeholder')}
            required
          />

          <Input
            label={t('adres')}
            value={form.address}
            onChange={v => updateForm('address', v)}
            placeholder={t('adres_placeholder')}
            required
          />

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 2 }}>
              <Input
                label={t('stad')}
                value={form.city}
                onChange={v => updateForm('city', v)}
                placeholder={t('stad_placeholder')}
                required
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input
                label={t('postcode')}
                value={form.postal_code}
                onChange={v => updateForm('postal_code', v.toUpperCase())}
                placeholder={t('postcode_placeholder')}
                required
              />
            </div>
          </div>

          {/* Connector type */}
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, color: 'var(--ink-soft)',
              letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8
            }}>
              {t('connector_type')} *
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {CONNECTOR_TYPES.map(type => (
                <button
                  key={type}
                  onClick={() => updateForm('connector_type', type)}
                  style={{
                    padding: '9px 14px', borderRadius: 10,
                    border: `1.5px solid ${form.connector_type === type ? 'var(--green)' : 'var(--cream-dark)'}`,
                    background: form.connector_type === type ? 'var(--green-pale)' : 'white',
                    color: form.connector_type === type ? 'var(--green)' : 'var(--ink-muted)',
                    fontWeight: 700, cursor: 'pointer', fontSize: 13,
                    fontFamily: 'DM Sans, sans-serif'
                  }}>
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Speed */}
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, color: 'var(--ink-soft)',
              letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8
            }}>
              {t('snelheid')} (kW) *
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {SPEEDS.map(speed => (
                <button
                  key={speed}
                  onClick={() => updateForm('speed_kw', speed)}
                  style={{
                    padding: '9px 14px', borderRadius: 10,
                    border: `1.5px solid ${form.speed_kw === speed ? 'var(--green)' : 'var(--cream-dark)'}`,
                    background: form.speed_kw === speed ? 'var(--green-pale)' : 'white',
                    color: form.speed_kw === speed ? 'var(--green)' : 'var(--ink-muted)',
                    fontWeight: 700, cursor: 'pointer', fontSize: 13,
                    fontFamily: 'DM Sans, sans-serif'
                  }}>
                  {speed}kW
                </button>
              ))}
            </div>
          </div>

          <Input
            label={`${t('prijs')} *`}
            type="number"
            value={form.price_per_kwh}
            onChange={v => updateForm('price_per_kwh', v)}
            placeholder={t('prijs_placeholder')}
            required
          />

          <Button fullWidth onClick={handleStep1} style={{ marginTop: 8 }}>
            Verder → Beschikbaarheid
          </Button>

        </div>
      ) : (
        // STEP 2: Availability calendar
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Info */}
          <div style={{
            background: 'var(--green-pale)',
            border: '1px solid var(--green-mid)',
            borderRadius: 12,
            padding: '10px 14px',
            fontSize: 13,
            color: 'var(--ink-soft)',
            lineHeight: 1.5
          }}>
            💡 Selecteer de dagen waarop jouw laadpaal beschikbaar is. Rijders kunnen alleen boeken op deze dagen en tijden.
          </div>

          {/* Quick presets */}
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, color: 'var(--ink-soft)',
              letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8
            }}>
              Snelle instelling
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {PRESETS.map(preset => (
                <button
                  key={preset.label}
                  onClick={() => applyPreset(preset)}
                  style={{
                    flex: 1,
                    padding: '9px 8px',
                    borderRadius: 10,
                    border: '1.5px solid var(--green-mid)',
                    background: 'var(--green-pale)',
                    color: 'var(--green)',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontFamily: 'DM Sans, sans-serif'
                  }}>
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Day toggle buttons */}
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, color: 'var(--ink-soft)',
              letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8
            }}>
              Beschikbare dagen {selectedDayCount > 0 && `(${selectedDayCount} geselecteerd)`}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {DAYS.map(day => {
                const selected = !!availability[day.id]
                return (
                  <button
                    key={day.id}
                    onClick={() => toggleDay(day.id)}
                    style={{
                      flex: 1,
                      padding: '10px 4px',
                      borderRadius: 10,
                      border: `1.5px solid ${selected ? 'var(--green)' : 'var(--cream-dark)'}`,
                      background: selected ? 'var(--green)' : 'white',
                      color: selected ? 'white' : 'var(--ink-muted)',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: 11,
                      fontFamily: 'DM Sans, sans-serif',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 3,
                      transition: 'all 0.15s'
                    }}>
                    {day.short}
                    {selected && (
                      <div style={{
                        width: 4, height: 4,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.7)'
                      }} />
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Time slots per selected day */}
          {DAYS.filter(day => availability[day.id]).map(day => (
            <div key={day.id} style={{
              background: 'white',
              border: '1.5px solid var(--green-mid)',
              borderRadius: 14,
              padding: '12px 14px'
            }}>
              <div style={{
                fontWeight: 700,
                color: 'var(--green)',
                fontSize: 13,
                marginBottom: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span>📅 {day.full}</span>
                <button
                  onClick={() => addSlot(day.id)}
                  style={{
                    background: 'var(--green-pale)',
                    border: '1px solid var(--green-mid)',
                    borderRadius: 8,
                    padding: '3px 10px',
                    color: 'var(--green)',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontSize: 11,
                    fontFamily: 'DM Sans, sans-serif'
                  }}>
                  + tijdslot
                </button>
              </div>

              {availability[day.id].map((slot, slotIndex) => (
                <div key={slotIndex} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: slotIndex < availability[day.id].length - 1 ? 8 : 0
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: 10, color: 'var(--ink-muted)',
                      fontWeight: 600, marginBottom: 3,
                      textTransform: 'uppercase', letterSpacing: 0.5
                    }}>
                      Van
                    </div>
                    <input
                      type="time"
                      value={slot.start}
                      onChange={e => updateSlot(day.id, slotIndex, 'start', e.target.value)}
                      style={{
                        width: '100%',
                        background: 'var(--cream)',
                        border: '1.5px solid var(--cream-dark)',
                        borderRadius: 10,
                        padding: '9px 10px',
                        color: 'var(--ink)',
                        fontSize: 14,
                        fontFamily: 'DM Sans, sans-serif'
                      }}
                    />
                  </div>
                  <div style={{ color: 'var(--ink-muted)', marginTop: 18, fontWeight: 700 }}>–</div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: 10, color: 'var(--ink-muted)',
                      fontWeight: 600, marginBottom: 3,
                      textTransform: 'uppercase', letterSpacing: 0.5
                    }}>
                      Tot
                    </div>
                    <input
                      type="time"
                      value={slot.end}
                      onChange={e => updateSlot(day.id, slotIndex, 'end', e.target.value)}
                      style={{
                        width: '100%',
                        background: 'var(--cream)',
                        border: '1.5px solid var(--cream-dark)',
                        borderRadius: 10,
                        padding: '9px 10px',
                        color: 'var(--ink)',
                        fontSize: 14,
                        fontFamily: 'DM Sans, sans-serif'
                      }}
                    />
                  </div>
                  {availability[day.id].length > 1 && (
                    <button
                      onClick={() => removeSlot(day.id, slotIndex)}
                      style={{
                        background: '#fee2e2',
                        border: 'none',
                        borderRadius: 8,
                        width: 32,
                        height: 32,
                        cursor: 'pointer',
                        color: 'var(--red)',
                        fontSize: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        marginTop: 18,
                        fontFamily: 'DM Sans, sans-serif'
                      }}>
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          ))}

          {selectedDayCount === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '20px 0',
              color: 'var(--ink-muted)',
              fontSize: 13
            }}>
              Selecteer dagen hierboven of kies een snelle instelling
            </div>
          )}

          <Button
            fullWidth
            onClick={handleSubmit}
            disabled={loading || selectedDayCount === 0}
            style={{ marginTop: 8 }}>
            {geocoding
              ? '📍 Adres opzoeken...'
              : loading
                ? t('laden')
                : `${t('laadpaal_opslaan')} 🔌`}
          </Button>

        </div>
      )}
    </div>
  )
}

export default ChargerForm
